import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileArchive, Loader2, X, XCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { ZIP_JOB_EVENT, decideZipJob, refreshAfterImport, zipJob, type ZipJob } from '@/lib/zip-transfer';

/** Progress + result of ZIP imports, bottom-left of the shell. One row per job. */
export function ZipImportPanel() {
  const [jobs, setJobs] = useState<ZipJob[]>([]);
  // Each paused import is asked about once, even though polling keeps seeing it.
  const asked = useRef(new Set<string>());

  useEffect(() => {
    const add = (e: Event) => setJobs((cur) => [...cur.filter((j) => j.id !== (e as CustomEvent<ZipJob>).detail.id), (e as CustomEvent<ZipJob>).detail]);
    window.addEventListener(ZIP_JOB_EVENT, add);
    return () => window.removeEventListener(ZIP_JOB_EVENT, add);
  }, []);

  const running = jobs.filter((j) => j.status !== 'done' && j.status !== 'failed').map((j) => j.id).join(',');
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(async () => {
      for (const id of running.split(',')) {
        const next = await zipJob(id).catch(() => null);
        if (!next) continue;
        setJobs((cur) => cur.map((j) => (j.id === id ? next : j)));
        if (next.status === 'confirm' && !asked.current.has(id)) {
          asked.current.add(id);
          const sample = next.conflictNames.join('\n') + (next.conflicts > next.conflictNames.length ? '\n…' : '');
          const overwrite = window.confirm(t('zip.overwriteConfirm', { n: next.conflicts, name: next.zipName, files: sample }));
          await decideZipJob(id, overwrite).catch(() => {});
        }
        if (next.status === 'done' || next.status === 'failed') refreshAfterImport();
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [running]);

  if (!jobs.length) return null;
  return (
    <div className="fixed bottom-4 left-4 z-50 flex w-80 flex-col gap-2">
      {jobs.map((j) => {
        const busy = j.status !== 'done' && j.status !== 'failed';
        const pct = j.total ? Math.round((j.done / j.total) * 100) : 0;
        return (
          <div key={j.id} className="rounded-lg border line surface-card p-3 text-[12px] shadow-lg" role="status">
            <div className="flex items-start gap-2">
              {busy ? (
                <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-brand-500" />
              ) : j.status === 'failed' ? (
                <XCircle className="mt-0.5 size-4 shrink-0 text-red-600" />
              ) : (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-brand-600" />
              )}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1 truncate font-medium text-strong">
                  <FileArchive className="size-3.5 shrink-0 text-faint" /> {j.zipName}
                </p>
                {busy && (
                  <>
                    <p className="text-muted">
                      {j.status === 'importing'
                        ? t('zip.importing', { done: j.done, total: j.total })
                        : j.status === 'confirm'
                          ? t('zip.waitingDecision', { n: j.conflicts })
                          : t('zip.unpacking')}
                    </p>
                    <div className="mt-1 h-1.5 overflow-hidden rounded bg-gray-200 dark:bg-white/10">
                      <div className="h-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </>
                )}
                {j.status === 'done' && (
                  <p className="text-muted">
                    {t('zip.done', { created: j.created, folders: j.foldersCreated })}
                    {j.replaced > 0 && ` · ${t('zip.replaced', { n: j.replaced })}`}
                    {j.skipped.length > 0 && ` · ${t('zip.skipped', { n: j.skipped.length })}`}
                    {j.failed.length > 0 && ` · ${t('zip.failed', { n: j.failed.length })}`}
                  </p>
                )}
                {j.status === 'failed' && <p className="text-red-700">{j.error ?? t('zip.importFailed')}</p>}
                {!busy && (j.skipped.length > 0 || j.failed.length > 0) && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-brand-600">{t('zip.details')}</summary>
                    <ul className="mt-1 max-h-40 overflow-auto text-[11px] text-muted">
                      {j.failed.map((f) => (
                        <li key={`f-${f.name}`} className="text-red-700">{f.name}: {f.reason}</li>
                      ))}
                      {j.skipped.map((f) => (
                        <li key={`s-${f.name}`}>{f.name}: {t('zip.alreadyExists')}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              {!busy && (
                <button type="button" onClick={() => setJobs((cur) => cur.filter((x) => x.id !== j.id))} aria-label={t('training.close')} className="rounded p-0.5 text-faint hover:text-strong">
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
